import { useQuery } from '@tanstack/react-query';
import { engineerEvaluationApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { EngineerEvaluationsParams, EngineerEvaluationDetailParams } from '../types/api.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニア評価一覧をTanStack Query経由で取得。四半期・評価タイプ・グループでフィルタ
 * @graph-connects /engineer-evaluations [calls] エンジニア評価一覧取得API
 * @graph-connects engineerEvaluationApi.getEvaluations [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useEngineerEvaluations(params?: EngineerEvaluationsParams) {
  return useQuery({
    queryKey: queryKeys.engineerEvaluation.evaluations(params),
    queryFn: () => engineerEvaluationApi.getEvaluations(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニア評価詳細をTanStack Query経由で取得。メール・四半期・評価タイプで特定の評価結果を返す
 * @graph-connects /engineer-evaluations/detail [calls] エンジニア評価詳細取得API
 * @graph-connects engineerEvaluationApi.getDetail [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useEngineerEvaluationDetail(params: EngineerEvaluationDetailParams | null) {
  const safeParams = params ?? { email: '', quarter: '' };
  return useQuery({
    queryKey: queryKeys.engineerEvaluation.detail(safeParams),
    queryFn: () => engineerEvaluationApi.getDetail(safeParams),
    enabled: !!params,
  });
}

/**
 * 自分の全四半期評価サマリーを取得
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分の全四半期スキル評価サマリーをTanStack Query経由で一括取得
 * @graph-connects /engineer-evaluations/my-summary [calls] 自分の全Qサマリー取得API
 * @graph-connects engineerEvaluationApi.getMySummary [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMyEvaluationSummary() {
  return useQuery({
    queryKey: queryKeys.engineerEvaluation.mySummary(),
    queryFn: () => engineerEvaluationApi.getMySummary(),
  });
}
